import { t } from 'src/i18n';
import type { MediaPermissionResult, MediaPermissionState } from './cameraPermissions-types.js';

export type { MediaPermissionState, MediaPermissionResult } from './cameraPermissions-types.js';

/**
 * Sjekker tillatelse for kamera og mikrofon på tvers av nettlesere.
 */
export async function checkMediaPermissions(): Promise<MediaPermissionResult> {
  // Safari har ikke navigator.permissions
  if (!('permissions' in navigator)) {
    return {
      camera: 'unsupported',
      microphone: 'unsupported',
    };
  }

  const result: MediaPermissionResult = {
    camera: 'unsupported',
    microphone: 'unsupported',
  };

  try {
    const mic = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    const cam = await navigator.permissions.query({ name: 'camera' as PermissionName });
    result.microphone = mic.state as MediaPermissionState;
    result.camera = cam.state as MediaPermissionState;
  } catch {
    // Noen nettlesere (f.eks. Firefox) kaster her
    result.camera = 'unsupported';
    result.microphone = 'unsupported';
  }

  return result;
}

/**
 * Forsøker å be om tilgang (viser prompt hvis nødvendig).
 * Returnerer true hvis bruker godtar.
 */
export async function requestMediaAccess(): Promise<boolean> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    // Stopp umiddelbart for å frigjøre ressurser
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'NotAllowedError') {
      console.warn('Brukeren nektet tilgang til kamera/mikrofon.');
    } else {
      console.warn('Feil ved forespørsel om media:', err); // i18n-exempt: konsoll-logg
    }
    return false;
  }
}

/**
 * Kombinert metode – sjekker status, ber om tilgang om nødvendig,
 * og håndterer brukerveiledning hvis blokkert.
 */
export async function ensureMediaAccess(): Promise<boolean> {
  const status = await checkMediaPermissions();

  // Safari fallback – prøv direkte
  if (status.camera === 'unsupported' || status.microphone === 'unsupported') {
    console.log('Permissions API ikke støttet – prøver direkte forespørsel.'); // i18n-exempt: konsoll-logg
    return requestMediaAccess();
  }

  if (status.camera === 'granted' && status.microphone === 'granted') {
    return true;
  }

  if (status.camera === 'denied' || status.microphone === 'denied') {
    alert(t('text.mediaAccessBlocked'));
    return false;
  }

  // Hvis status = "prompt"
  return requestMediaAccess();
}
